<template>
  <div>
    <div v-if="processed">
      <h1>{{ $t(i18n.changes_pending_label) }}</h1>
      <info-block variant="warning" icon="alert">
        <div v-html="mediaItemChangeCocResult.htmlContent"></div>
      </info-block>

      <button-group top-spacing="xs">
        <button-fragment type="button" @click="closeModal">
          {{ $t(i18n.confirm_label) }}
        </button-fragment>
      </button-group>
    </div>
    <div v-else>
      <form>
        <text-input-fragment
          key="cocId"
          :disabled="isProcessing || validatingCoc"
          :label="$t(i18n.new_coc_label)"
          v-model="formValues.cocId"
          :is-invalid="
            isFieldInvalid($v.formValues.cocId) ||
              isFieldInvalid($v.cocValidation)
          "
          @input="setCoc"
          margin-top="0"
        >
          <template #result>
            <field-error-messages :validation-state="$v.formValues.cocId" />
          </template>
        </text-input-fragment>

        <text-input-fragment
          key="cocSubId"
          :disabled="isProcessing || validatingCoc"
          :label="$t(i18n.new_sub_coc_label)"
          v-model="formValues.cocSubId"
          :is-invalid="
            isFieldInvalid($v.formValues.cocSubId) ||
              isFieldInvalid($v.cocValidation)
          "
          @input="setCocSub"
        >
          <template #result v-if="!validatingCoc">
            <p v-if="isCocValid">
              <br />{{ formValues.companyData.companyName }}<br />
              {{ formValues.companyData.companyStreetName }}
              {{ formValues.companyData.companyHouseNumber }} <br />
              {{ formValues.companyData.companyPostalCode }}
              {{ formValues.companyData.companyCityName }}
            </p>
            <field-error-messages :validation-state="$v.formValues.cocSubId" />
            <field-error-messages :validation-state="$v.cocValidation.data" />
            <field-error-messages
              :validation-state="$v.formValues.companyData.companyName"
            />
          </template>
        </text-input-fragment>

        <fragment-separator />

        <radio-group-fragment
          key="cocChangeType"
          group-name="radioGroupCocChangeTypes"
          :disabled="isProcessing"
          :label="$t(i18n.type_change_label)"
          :options="radioGroupCocChangeTypes"
          v-model="formValues.cocChangeType"
          :is-invalid="isFieldInvalid($v.formValues.cocChangeType)"
        >
          <template #result>
            <field-error-messages
              :validation-state="$v.formValues.cocChangeType"
            />
          </template>
        </radio-group-fragment>

        <radio-group-fragment
          key="shouldCompanyDepartmentsBeNotified"
          group-name="shouldCompanyDepartmentsBeNotified"
          :disabled="isProcessing"
          :label="$t(i18n.gdpr_share_confirmation_label)"
          :options="genericYesNoOptions"
          v-model="formValues.shouldCompanyDepartmentsBeNotified"
          :is-invalid="isFieldInvalid($v.formValues.shouldCompanyDepartmentsBeNotified)">
          <template #result>
            <field-error-messages :validation-state="$v.formValues.shouldCompanyDepartmentsBeNotified" />
          </template>
        </radio-group-fragment>

        <radio-group-fragment
          key="roadTrafficServiceNumberChange"
          group-name="roadTrafficServiceNumberChange"
          :disabled="isProcessing"
          :label="$t(i18n.rdw_number_changed_label)"
          :options="genericYesNoOptions"
          v-model="formValues.roadTrafficServiceNumberChange"
          :is-invalid="isFieldInvalid($v.formValues.roadTrafficServiceNumberChange)">
          <template #result>
            <field-error-messages :validation-state="$v.formValues.roadTrafficServiceNumberChange" />
          </template>
        </radio-group-fragment>

        <text-input-fragment
            v-if="formValues.roadTrafficServiceNumberChange === 'y'"
            key="roadTrafficServiceNumberChange"
            :disabled="isProcessing"
            :label="$t(i18n.rdw_number_label)"
            v-model="formValues.roadTrafficServiceNumber"
            :is-invalid="isFieldInvalid($v.formValues.roadTrafficServiceNumber)">
          <template #result>
            <field-error-messages :validation-state="$v.formValues.roadTrafficServiceNumber" />
          </template>
        </text-input-fragment>

        <fragment-separator />

        <date-input-fragment
          key="startDate"
          :disabled="isProcessing"
          :label="$t(i18n.startdate_label)"
          v-model="formValues.startDate"
          :is-invalid="isFieldInvalid($v.formValues.startDate)" >
          <template #result>
            <field-error-messages :validation-state="$v.formValues.startDate" />
          </template>
        </date-input-fragment>

        <fragment-separator />

        <text-input-fragment
          key="fullName"
          size="max"
          :disabled="isProcessing"
          :label="$t(i18n.fullname_label)"
          v-model="formValues.fullName"
          :is-invalid="isFieldInvalid($v.formValues.fullName)">
          <template #result>
            <field-error-messages :validation-state="$v.formValues.fullName" />
          </template>
        </text-input-fragment>

        <text-input-fragment
          key="emailAddress"
          size="max"
          :disabled="isProcessing"
          :label="$t(i18n.emailaddress_label)"
          v-model="formValues.emailAddress"
          :is-invalid="isFieldInvalid($v.formValues.emailAddress)">
          <template #result>
            <field-error-messages :validation-state="$v.formValues.emailAddress" />
          </template>
        </text-input-fragment>

        <text-input-fragment
          key="telephone"
          :disabled="isProcessing"
          :label="$t(i18n.phone_label)"
          v-model="formValues.telephone"
          :is-invalid="isFieldInvalid($v.formValues.telephone)">
          <template #result>
            <field-error-messages :validation-state="$v.formValues.telephone" />
          </template>
        </text-input-fragment>

        <fragment-separator />

        <div v-if="error">
          <p class="error-message">{{ error }}</p>

          <fragment-separator />
        </div>

        <button-group direction="right">
          <button-fragment
            :disabled="isProcessing"
            type="button"
            key="submit-button"
            @click="submitCalled()"
          >
            {{ $t(i18n.submit_label) }}
          </button-fragment>
        </button-group>
      </form>
    </div>
  </div>
</template>

<script>
import { Api, Utils } from "@bvm/core";
import {
  FragmentSeparator,
  TextInputFragment,
  RadioGroupFragment,
  DateInputFragment,
  ButtonFragment,
  ButtonGroup,
  InfoBlock
} from "@bvm/forms";
import {
  email,
  required,
  numeric,
  minLength,
  maxLength,
  requiredIf
} from "vuelidate/lib/validators";
import FieldErrorMessages from "../shared/field-error-messages";
import { changeCocRequest } from "../../api/change-request-api";
import { cocApi } from "../../api/coc-api";
import { CHANGE_COC_REQUEST } from "../../config/funnel-id.constants";

const i18nBuilder = new Utils.I18n.I18nBuilder("change_coc")
  .add("invalid_label")
  .add("changes_pending_label")
  .add("new_coc_label")
  .add("new_sub_coc_label")
  .add("type_change_label")
  .add("type_change_merger_label")
  .add("type_change_fusion_label")
  .add("type_change_legal_label")
  .add("type_change_other_label")
  .add("rdw_number_changed_label")
  .add("rdw_number_label")
  .add("gdpr_share_confirmation_label")
  .add("yes_label")
  .add("no_label")
  .add("startdate_label")
  .add("fullname_label")
  .add("emailaddress_label")
  .add("phone_label")
  .add("submit_label")
  .add("confirm_label")
  .add("error_label");

const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
  .add("email")
  .add("required")
  .add("coc")
  .add("NO_COMPANY_FOUND")
  .add("TOO_MANY_REQUESTS")
  .add("INSURANCE_REQUEST_IMPOSSIBLE");

export const messageKeys = [
  ...i18nBuilder.getKeys(),
  ...i18nValidationBuilder.getKeys(),
];

const mediaTags = {
  CHANGE_COC_RESULT: "CHANGE_COC_RESULT"
};

export default {
  name: "change-coc-form",
  components: {
    FragmentSeparator,
    TextInputFragment,
    RadioGroupFragment,
    DateInputFragment,
    ButtonFragment,
    ButtonGroup,
    InfoBlock,
    FieldErrorMessages
  },
  data() {
    return {
      i18n: i18nBuilder.getData(),
      processed: false,
      isProcessing: false,
      mediaItemChangeCocResult: "",
      error: "",
      cocValidation: "NO_COMPANY_FOUND",
      validatingCoc: false,
      formValues: {
        cocId: "",
        cocSubId: "",
        companyData: {
          companyName: "",
          companyStreetName: "",
          companyHouseNumber: "",
          companyPostalCode: "",
          companyCityName: ""
        },
        cocChangeType: "",
        roadTrafficServiceNumberChange: "",
        roadTrafficServiceNumber: "",
        shouldCompanyDepartmentsBeNotified: "",
        startDate: "",
        fullName: "",
        emailAddress: "",
        telephone: ""
      }
    };
  },
  validations() {
    return {
      formValues: {
        cocId: {
          required,
          numeric,
          minLength: minLength(8),
          maxLength: maxLength(8)
        },
        cocSubId: {
          required,
          numeric,
          minLength: minLength(12),
          maxLength: maxLength(12)
        },
        companyData: {
          companyName: { required }
        },
        cocChangeType: { required },
        roadTrafficServiceNumberChange: { required },
        roadTrafficServiceNumber: {
          required: requiredIf(() =>
              this.formValues.roadTrafficServiceNumberChange === 'y'
          )
        },
        shouldCompanyDepartmentsBeNotified: { required },
        startDate: { required },
        fullName: { required },
        emailAddress: { required, email },
        telephone: { required }
      },
      cocValidation: {
        data: {
          ["coc_" + this.cocValidation.errorCode]: requiredIf(
            () =>
              !this.$v.formValues.cocId.$invalid &&
              !this.$v.formValues.cocSubId.$invalid
          )
        }
      }
    };
  },
  mounted() {
    this.error = "";

    Api.MediaItem.getMediaItem(
      "bvmop basic clean display on",
      "article",
      true,
      mediaTags.CHANGE_COC_RESULT
    ).then(mediaItem => {
      this.mediaItemChangeCocResult = mediaItem;
    });
  },
  methods: {
    isFieldInvalid(field) {
      return field.$dirty && field.$invalid;
    },
    setCoc(value) {
      this.formValues.cocId = value;
      this.validateCocExternally();
    },
    setCocSub(value) {
      this.formValues.cocSubId = value;
      this.validateCocExternally();
    },
    validateCocExternally() {
      if (
        !this.$v.formValues.cocId.$invalid &&
        !this.$v.formValues.cocSubId.$invalid
      ) {
        this.getCompanyRdcData();
      }
    },
    getCompanyRdcData() {
      this.validatingCoc = true;
      this.formValues.companyData = {};
      cocApi(
        this.formValues.cocId,
        this.formValues.cocSubId,
        CHANGE_COC_REQUEST
      )
        .then(validation => {
          this.cocValidation = validation;
          if (this.cocValidation.data) {
            this.formValues.companyData = {
              companyName: validation.data.name,
              companyStreetName: validation.data.organizationAddress.streetName,
              companyHouseNumber:
                validation.data.organizationAddress.houseNumber +
                (validation.data.organizationAddress.houseNumberAddition || ""),
              companyPostalCode: validation.data.organizationAddress.postalCode,
              companyCityName: (
                validation.data.organizationAddress.city || ""
              ).toLowerCase()
            };
          }
        })
        .finally(() => {
          this.$v.formValues.cocId.$touch();
          this.$v.cocValidation.$touch();
          this.validatingCoc = false;
        });
    },
    sendData() {
      const preparedData = {
        cocId: this.formValues.cocId,
        cocSubId: this.formValues.cocSubId,
        cocChangeType: this.formValues.cocChangeType,
        roadTrafficServiceNumberChange:
          this.formValues.roadTrafficServiceNumberChange === "y",
        shouldCompanyDepartmentsBeNotified:
          this.formValues.shouldCompanyDepartmentsBeNotified === "y",
        startDate: this.formValues.startDate,
        fullName: this.formValues.fullName,
        emailAddress: this.formValues.emailAddress,
        telephone: this.formValues.telephone
      };

      // Only set roadTrafficServiceNumber when changed
      if (preparedData.roadTrafficServiceNumberChange) {
        preparedData.roadTrafficServiceNumber = this.formValues.roadTrafficServiceNumber;
      }

      this.error = "";
      this.isProcessing = true;

      changeCocRequest(preparedData)
        .then(result => {
          if (result.status === 200) {
            this.processed = true;
          }
        })
        .catch(() => {
          this.processed = false;
          this.error = this.$t(this.i18n.error_label);
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    submitCalled() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.sendData();
      }
    },
    closeModal() {
      this.$emit("close");
    }
  },
  computed: {
    radioGroupCocChangeTypes() {
      return {
        TAKE_OVER: this.$t(this.i18n.type_change_merger_label),
        MERGER: this.$t(this.i18n.type_change_fusion_label),
        CHANGE_LEGAL_FORM: this.$t(this.i18n.type_change_legal_label),
        OTHER: this.$t(this.i18n.type_change_other_label)
      };
    },
    genericYesNoOptions() {
      return {
        y: this.$t(this.i18n.yes_label),
        n: this.$t(this.i18n.no_label)
      };
    },
    isCocValid() {
      return (
        !this.$v.formValues.cocId.$invalid &&
        !this.$v.cocValidation.$invalid &&
        this.formValues.companyData.companyName
      );
    }
  }
};
</script>
